// Phase 11d.5 — /settings/referrals
import { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gift, Copy, Send, CheckCircle2, Clock, Sparkles, ChevronLeft } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useAppStore } from "@/store/useAppStore";

function StatusPill({ status }) {
  const map = {
    Rewarded: { c: "bg-emerald-100 text-emerald-700 ring-emerald-200", Icon: CheckCircle2 },
    "Signed up": { c: "bg-indigo-100 text-indigo-700 ring-indigo-200", Icon: Sparkles },
    Pending: { c: "bg-amber-100 text-amber-700 ring-amber-200", Icon: Clock },
  };
  const { c, Icon } = map[status] || map.Pending;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 text-[10.5px] font-semibold ${c}`} data-testid={`ref-status-${status}`}>
      <Icon size={10} /> {status}
    </span>
  );
}

StatusPill.propTypes = {
  status: PropTypes.string.isRequired,
};

export default function SettingsReferrals() {
  const navigate = useNavigate();
  const { tenant } = useAppStore();
  const shareLink = tenant?.slug
    ? `https://sellix.app/r/${tenant.slug}`
    : "https://sellix.app/r/your-workspace";
  const [rows, setRows] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(
    "Hey — I've been using Sellyx as my personal AI SDR. Maya replies to my WhatsApp + LinkedIn 24/7, in my voice. Worth a 6-minute setup. If you sign up via my link, we both get a free month.",
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch {
      /* best effort */
    }
    toast.success("Referral link copied · share with another founder");
  };

  const sendReferral = () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setRows((p) => [
      {
        id: `ref-${Date.now()}`,
        email: email.trim(),
        sentAt: new Date().toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" }),
        status: "Pending",
      },
      ...p,
    ]);
    setSendOpen(false);
    setEmail("");
    toast.success(`Invite sent to ${email.trim()}`);
  };

  const sent = rows.length;
  const signedUp = rows.filter((r) => r.status === "Signed up" || r.status === "Rewarded").length;

  return (
    <div className="flex flex-col min-h-0 flex-1 bg-white">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => navigate("/settings/team")} data-testid="ref-back">
          <ChevronLeft size={14} />
        </Button>
        <div>
          <h1 className="text-[18px] font-semibold tracking-tight text-slate-900">Referrals</h1>
          <p className="text-[12px] text-slate-500">Refer a founder, get a month free.</p>
        </div>
      </div>

      <div className="p-6 max-w-4xl space-y-5 overflow-y-auto" data-testid="settings-referrals">
        <div className="rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-indigo-700 text-white p-7 shadow-sm" data-testid="ref-hero">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
            <Gift size={18} />
          </div>
          <h2 className="mt-3 text-[26px] font-semibold tracking-tight leading-tight">Refer a founder, get a month free.</h2>
          <p className="mt-1 text-[13.5px] text-indigo-100 max-w-xl leading-relaxed">
            Share Sellyx with another founder. When they sign up, you both get a month on us.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Sent</div>
            <div className="mt-1 text-[24px] font-semibold tabular-nums text-slate-900 leading-none">{sent}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Signed up</div>
            <div className="mt-1 text-[24px] font-semibold tabular-nums text-indigo-700 leading-none">{signedUp}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-[10.5px] uppercase tracking-wider text-emerald-700 font-semibold">Months earned</div>
            <div className="mt-1 text-[24px] font-semibold tabular-nums text-emerald-700 leading-none">
              {rows.filter((r) => r.status === "Rewarded").length}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ref-link-card">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Your unique link</div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-[12.5px] font-mono text-slate-800 truncate" data-testid="ref-link-url">
              {shareLink}
            </code>
            <Button size="sm" className="h-9" onClick={copyLink} data-testid="ref-copy">
              <Copy size={12} /> Copy
            </Button>
            <Button size="sm" variant="outline" className="h-9" onClick={() => setSendOpen(true)} data-testid="ref-send-button">
              <Send size={12} /> Send referral
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
            <div className="text-[12.5px] font-semibold text-slate-900">Tracking</div>
            <div className="text-[10.5px] text-slate-500">{sent} sent · {signedUp} signed up</div>
          </div>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">
                <th className="px-4 py-2">Email</th>
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-[12.5px] text-slate-500">
                    No referrals sent yet. Share your link to get started.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`ref-row-${r.id}`}>
                    <td className="px-4 py-2.5 font-mono text-slate-700">{r.email}</td>
                    <td className="px-3 py-2.5 text-slate-500 tabular-nums">{r.sentAt}</td>
                    <td className="px-3 py-2.5">
                      <StatusPill status={r.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="sm:max-w-[460px]" data-testid="ref-send-modal">
          <DialogHeader>
            <DialogTitle>Refer a founder</DialogTitle>
            <DialogDescription className="sr-only">Send a personalized referral email with your unique signup link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input type="email" placeholder="founder@theirstartup.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-9" data-testid="ref-email-input" />
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="text-[12.5px]" data-testid="ref-message-input" />
            <div className="text-[10.5px] text-slate-500 leading-snug">Your unique referral link will be appended automatically.</div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={sendReferral} data-testid="ref-send-confirm">
              <Send size={12} /> Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Badge className="hidden">noop</Badge>
    </div>
  );
}
